import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { publicAPI } from '../../services/api';
import StudentSignupForm from '../publicaccess/StudentSignupForm';

const StudentSignupPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [validating, setValidating] = useState(true);
  const [validationError, setValidationError] = useState(null);
  const [invitationData, setInvitationData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const invitationToken = searchParams.get('token');
    
    if (!invitationToken) {
      setValidationError('No invitation token provided');
      setValidating(false);
      return;
    }

    setToken(invitationToken);
    validateToken(invitationToken);
  }, [searchParams]);

  const validateToken = async (token) => {
    try {
      console.log('Validating invitation token:', token);
      const response = await publicAPI.validateToken(token);
      
      if (response.success) {
        console.log('Token is valid:', response.data);
        setInvitationData(response.data);
        setValidating(false);
      } else {
        setValidationError('Invalid invitation token');
        setValidating(false);
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      setValidationError(error.message || 'Failed to validate invitation token');
      setValidating(false);
    }
  };

  const handleSignupSubmit = async (formData) => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      console.log('Submitting student signup form:', formData);
      
      // Add token to form data
      const signupData = {
        token,
        ...formData
      };
      
      const response = await publicAPI.studentSignup(signupData);
      
      if (response.success) {
        console.log('Student signup successful:', response.data);
        
        // Show success message and redirect to login
        alert('Registration successful! You can now log in with your credentials.');
        
        // Redirect to login page or home
        window.location.href = '/';
      } else {
        // If response has an error but didn't throw
        throw new Error(response.error || 'Signup failed');
      }
    } catch (error) {
      console.error('Student signup failed:', error);
      
      // Store error and redirect to Support page
      setSubmitError(error.message || 'An error occurred during signup');
      
      // Navigate to support page with error info
      navigate('/support', {
        state: {
          error: {
            message: error.message || 'Student signup failed',
            type: 'signup',
            context: 'student_registration'
          }
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '2rem'
        }}>
          <div className="spinner" style={{
            width: '3rem',
            height: '3rem',
            margin: '0 auto 1rem',
            border: '4px solid #e5e7eb',
            borderTopColor: '#2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ fontSize: '1.125rem', color: '#6b7280' }}>
            Validating invitation...
          </p>
        </div>
      </div>
    );
  }

  if (validationError) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{
          maxWidth: '28rem',
          width: '100%',
          padding: '2rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '3rem',
            height: '3rem',
            margin: '0 auto 1rem',
            backgroundColor: '#fee2e2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            color: '#ef4444'
          }}>
            ✕
          </div>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: '0.5rem'
          }}>
            Invalid Invitation
          </h2>
          <p style={{
            fontSize: '1rem',
            color: '#6b7280',
            marginBottom: '1.5rem'
          }}>
            {validationError}
          </p>
          <p style={{
            fontSize: '0.875rem',
            color: '#9ca3af',
            marginBottom: '1.5rem'
          }}>
            The invitation link may have expired or is invalid. Please contact your instructor for a new invitation.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      padding: '2rem 1rem'
    }}>
      <div style={{
        maxWidth: '64rem',
        margin: '0 auto'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem'
        }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: '0.5rem'
          }}>
            Complete Your Registration
          </h1>
          <p style={{
            fontSize: '1.125rem',
            color: '#6b7280'
          }}>
            You've been invited by your instructor. Please fill out the form below to create your account.
          </p>
          {invitationData && (
            <p style={{
              fontSize: '0.875rem',
              color: '#9ca3af',
              marginTop: '0.5rem'
            }}>
              Registering as: <strong>{invitationData.email}</strong>
            </p>
          )}
        </div>

        {/* Error Message Display */}
        {submitError && (
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#991b1b'
          }}>
            <strong>Error:</strong> {submitError}
          </div>
        )}

        <StudentSignupForm
          onSubmit={handleSignupSubmit}
          prefilledEmail={invitationData?.email}
          isSubmitting={submitting}
        />
      </div>
    </div>
  );
};

export default StudentSignupPage;